import { LOG_ID, MEMBER_ID } from '@@types/common';
import Log from '@@types/Log';

import { SECOND } from '~/constants/date';
import logging from '~/stores/providers/logging';
import { propOr } from 'ramda';
import ReactGA from 'react-ga4';

let _userId = null;
let _tenantId = null;

function maskId(id: string) {
  return id.replace(/\d{10}(\d{5})$/, '**********$1');
}

const setUserInfo = ({ id, tenantId }) => {
  _userId = _userId || id;
  _tenantId = _tenantId || tenantId;
  ReactGA.set({ id: maskId(_userId), tenantId: _tenantId });
};

const initializeGA = (userInfo, gaId: string) => {
  ReactGA.initialize([{ trackingId: gaId }], { testMode: false });
  setUserInfo(userInfo);
};

const writeEvent = (params) => {
  if (_userId === null) return;
  ReactGA.event(params);
};

const writeError = (error) => {
  writeEvent({
    category: 'Script Error',
    action: propOr('', 'stack', error),
    label: propOr('', 'message', error),
  });
};

const writeRejectionError = (error) => {
  writeEvent({
    category: 'Rejection Script Error',
    action: propOr('', 'stack', error),
    label: propOr('', 'message', error),
  });
};

const writeRenderingError = (error, info) => {
  writeEvent({
    category: 'Rendering Error',
    action: propOr('', 'stack', error),
    label: propOr('', 'componentStack', info),
  });
};

const writeDiffEmailError = (error, info) => {
  writeEvent({
    category: 'Email Diff Error',
    action: info,
    label: error.stack,
  });
};

const writeXhrError = (label, url) => {
  writeEvent({
    category: 'Xhr',
    action: 'Error',
    label,
    url,
  });
};

const writeAxiosError = (errorType, errorJson, token, url) => {
  writeEvent({
    category: 'Axios Error',
    action: errorType,
    label: errorJson,
    url,
    token,
  });
};

const writeErrorAction = (action, label) => {
  writeEvent({
    category: 'Error',
    action,
    label,
  });
};

const writeOpenProfile = (memberId) => {
  writeEvent({
    category: '기타',
    action: '프로필 열기',
    label: memberId === _userId ? '내 프로필' : '타인 프로필',
  });
};

const writeSelectChannel = (selectPoint) => {
  writeEvent({
    category: '대화',
    action: '대화방 진입',
    label: selectPoint,
  });
};

const writeExecuteCommand = (commandName) => {
  writeEvent({
    category: '메시지',
    action: '전송(Command)',
    label: commandName,
  });
};

const writeCreateChannel = (type) => {
  writeEvent({
    category: '대화',
    action: '생성',
    label: type,
  });
};

const writeClick = ({ action, label = '' }) => {
  writeEvent({
    category: '클릭 지표',
    action,
    label,
  });
};

const writeSendSticker = ({ action, label }) => {
  writeEvent({
    category: '스티커',
    action,
    label,
  });
};

const writeThread = ({ action, label }) => {
  writeEvent({
    category: '스레드',
    action,
    label,
  });
};

const writeReconnectWebsocket = (isFetchChannels: string) => {
  writeEvent({
    category: '수집',
    action: 'reconnect websocket',
    label: isFetchChannels,
  });
};

const writeCloseWebsocket = (label: string) => {
  writeEvent({
    category: '수집',
    action: 'close websocket',
    label,
  });
};

const writeErrorWebsocket = (label: string) => {
  writeEvent({
    category: '수집',
    action: 'close websocket',
    label,
  });
};

const writeRequiredLogField = (fieldName: keyof Log, id: LOG_ID) => {
  writeEvent({
    category: '수집',
    action: `log invalid ${fieldName}`,
    label: id,
  });
};

const writeClearCacheError = () => {
  writeEvent({
    category: '수집',
    action: 'clear cache Error',
    label: '',
  });
};

const writeStatusSlow = (memberId: MEMBER_ID) => {
  writeEvent({
    category: '수집',
    action: 'update memberStatus slow',
    label: maskId(memberId),
  });
};

const writeAutoOpenThread = (use: boolean) => {
  writeEvent({
    category: '수집',
    action: `${use}`,
    label: `${Date.now()}`,
  });
};

const reloadTime = performance.now();
let timeoutId = {};
const writeSlow = (label: string) => {
  window.clearTimeout(timeoutId[label] || 0);
  const time = window.performance.now();
  timeoutId[label] = window.setTimeout(() => {
    const diff = window.performance.now() - time;
    if (diff > SECOND) {
      writeEvent({
        category: '수집',
        action: time - reloadTime < 10 * SECOND ? 'reload slow' : 'slow',
        label,
        value: diff,
      });
      logging.debug(time - reloadTime < 10 * SECOND ? 'reload slow' : 'slow', label, diff);
    }
  }, 1);
};

const writeWillMemberFetch = (label: string, value: number) => {
  writeEvent({
    category: '수집',
    action: '멤버 fetch 추가 목록',
    label,
    value,
  });
};

const writeGnbMenu = (action: string, label: string) => {
  writeEvent({
    category: 'GNB',
    action,
    label,
  });
};

const setFieldsObject = (fieldsObject) => {
  if (_userId === null) return;
  setUserInfo(fieldsObject);
};

const GA = {
  initializeGA,
  writeOpenProfile,
  writeEvent,
  writeCreateChannel,
  writeSelectChannel,
  writeExecuteCommand,
  writeXhrError,
  writeAxiosError,
  writeError,
  writeRejectionError,
  writeRenderingError,
  writeDiffEmailError,
  writeClick,
  writeSendSticker,
  setFieldsObject,
  writeErrorAction,
  writeReconnectWebsocket,
  writeCloseWebsocket,
  writeErrorWebsocket,
  writeRequiredLogField,
  writeClearCacheError,
  writeSlow,
  writeThread,
  writeStatusSlow,
  writeWillMemberFetch,
  writeAutoOpenThread,
  writeGnbMenu,
};
export default GA;
